import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card } from './ui/card';
import { countryCodes } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { fetchGeolocation, submitLead, getCountryFromGeo, getDialCodeFromCountryCode } from '../services/api';

const HeroForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);

  // Fetch geolocation on component mount
  useEffect(() => {
    const loadGeoLocation = async () => {
      const result = await fetchGeolocation();
      if (result.success && result.data) {
        setGeoData(result.data);
        
        // Set country code based on geolocation
        const countryCode = getCountryFromGeo(result.data);
        const dialCode = getDialCodeFromCountryCode(countryCode, countryCodes);
        
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode
        }));
      }
    };
    
    loadGeoLocation();
  }, []);

  const validatePhone = (phone) => {
    // Remove any non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (!phone) {
      return 'Phone number is required';
    }
    if (/[a-zA-Z]/.test(phone)) {
      return 'Phone number cannot contain letters';
    }
    if (digitsOnly.length > 10) {
      return 'Phone number cannot exceed 10 digits';
    }
    if (digitsOnly.length < 10) {
      return 'Phone number must be 10 digits';
    }
    return '';
  };

  const handlePhoneChange = (value) => {
    // Only allow numbers and formatting characters
    const sanitized = value.replace(/[^\d]/g, '');
    if (sanitized.length <= 10) {
      setFormData(prev => ({ ...prev, phone: sanitized }));
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone
    const phoneError = validatePhone(formData.phone);
    if (phoneError) {
      setErrors({ phone: phoneError });
      toast({
        title: "Validation Error",
        description: phoneError,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit lead to API
      const result = await submitLead(formData, geoData, countryCodes);

      if (result.success) {
        toast({
          title: "Success!",
          description: result.message || "Thank you! Redirecting...",
        });
        
        // Reset form
        setFormData({ name: '', email: '', countryCode: '+1', phone: '', message: '' });
        setErrors({});
        
        // Redirect after a short delay
        setTimeout(() => {
          window.location.href = result.redirect;
        }, 1500);
      } else {
        // Show error from API
        const errorMessage = result.error || "Failed to submit form. Please try again.";
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
        
        // Also show browser alert for visibility
        alert(`Submission Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Network error. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Card className="bg-white/95 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-2xl border-2 border-teal-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
          Start Your Trademark Registration
        </h3>
        <p className="text-gray-600">Fill out the form below and get expert assistance</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Name *
          </label>
          <Input
            required
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Your full name"
            className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Email *
          </label>
          <Input
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="your.email@example.com"
            className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Phone Number *
          </label>
          <div className="flex gap-2">
            <Select 
              value={formData.countryCode} 
              onValueChange={(value) => handleChange('countryCode', value)}
            >
              <SelectTrigger className="w-32 border-gray-300 focus:border-teal-500 focus:ring-teal-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.dial} value={item.dial}>
                    {item.dial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1">
              <Input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="1234567890"
                maxLength={10}
                className={`border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${
                  errors.phone ? 'border-red-500' : ''
                }`}
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">10 digits, numbers only</p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            What are you looking for protection? *
          </label>
          <Textarea
            required
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)}
            placeholder="E.g., Name, logo, slogan, or combination..."
            rows={4}
            className="border-gray-300 focus:border-teal-500 focus:ring-teal-500 resize-none"
          />
          <p className="text-gray-500 text-xs mt-1">Tell us what you want to protect</p>
        </div>

        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Register My Trademark →'}
        </Button>
        
        <p className="text-xs text-center text-gray-500">
          By submitting, you agree to our Terms of Service and Privacy Policy
        </p>
      </form>
    </Card>
  );
};

export default HeroForm;
